import React, { useState } from "react";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import { useParams } from "react-router-dom";
import { UPDATE_PAYMENT } from "../../../../api/apiContratos";
import Button from "../../../atoms/Button";
import { UPLOAD_FILE } from "../../../../api/apiFiles";

const ModalSubirComprobante = ({ pago, onClose, refetch }) => {
	const { id } = useParams();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [preview, setPreview] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);

	const uploadFile = async (file, path) => {
		if (!file) return null;

		const { name: filename, type: mimetype } = file;

		try {
			const { url, s3Key } = await UPLOAD_FILE(id, filename, mimetype, path);

			await fetch(url, {
				method: "PUT",
				headers: { "Content-Type": mimetype },
				body: file,
			});

			return {
				s3Key,
				filename,
				mimetype,
				title: filename,
			};
		} catch (error) {
			console.error("Error al subir archivo:", error);
			throw error;
		}
	};

	const handleSubmit = async (values) => {
		setIsSubmitting(true);

		try {
			if (!selectedFile) {
				toast.error("Por favor selecciona un archivo");
				setIsSubmitting(false);
				return;
			}

			const fileData = await uploadFile(selectedFile, "contratos/");

			const updateData = {
				status: "pagado",
				file: fileData,
			};

			await UPDATE_PAYMENT(pago.id, updateData);

			toast.success("Comprobante subido correctamente. Pago marcado como pagado.");
			refetch();
			onClose();
		} catch (error) {
			console.error("Error al subir comprobante:", error);
			toast.error("Error al subir el comprobante. Intenta de nuevo.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const validate = (values) => {
		const errors = {};
		if (!selectedFile) {
			errors.file = "El archivo es requerido";
		}
		return errors;
	};

	if (isSubmitting) {
		return (
			<div className="w-full h-40 flex justify-center items-center">
				<PropagateLoader color="red" />
			</div>
		);
	}

	return (
		<Formik
			initialValues={{ file: null }}
			validate={validate}
			onSubmit={handleSubmit}
		>
			{({ values, errors, setFieldValue }) => (
				<Form className="space-y-6">
					<div className="space-y-4">
						{/* Información del pago */}
						<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
							<p className="text-sm text-gray-600">
								Subiendo comprobante para el pago de:{" "}
							</p>
							<p className="font-bold text-lg text-blue-900">
								${pago.payment.toLocaleString("es-MX")}
							</p>
							<p className="text-xs text-gray-500 mt-2">
								Fecha: {new Date(pago.date).toLocaleDateString("es-MX")}
							</p>
						</div>
						<div
							className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
							onDrop={(e) => {
								e.preventDefault();
								const file = e.dataTransfer.files[0];
								if (file) {
									setSelectedFile(file);
									setPreview(file.name);
									setFieldValue("file", file);
								}
							}}
							onDragOver={(e) => e.preventDefault()}
						>
							<input
								type="file"
								id="file-input"
								accept="application/pdf,image/png,image/jpeg"
								onChange={(e) => {
									const file = e.target.files[0];
									if (file) {
										setSelectedFile(file);
										setPreview(file.name);
										setFieldValue("file", file);
									}
								}}
								style={{ display: "none" }}
							/>
							<label
								htmlFor="file-input"
								className="cursor-pointer block"
							>
								<p className="text-sm text-gray-600 mb-2">
									Arrastra tu comprobante aquí o{" "}
									<span className="text-blue-600 font-semibold">
										haz clic para seleccionar
									</span>
								</p>
								<p className="text-xs text-gray-500">
									PDF, PNG o JPG (máx. 5MB)
								</p>
							</label>

							{preview && (
								<div className="mt-4 p-2 bg-green-100 rounded">
									<p className="text-sm text-green-800 font-semibold">
										✓ {preview}
									</p>
								</div>
							)}
						</div>
						{errors.file && (
							<div className="text-red-600 text-sm border border-red-300 bg-red-50 p-3 rounded">
								{errors.file}
							</div>
						)}
					</div>
					<div className="flex gap-4 justify-end pt-4 border-t">
						<Button
							type="button"
							text="Cancelar"
							bgColor="red"
							onClick={onClose}
							disabled={isSubmitting}
						/>
						<Button
							type="submit"
							text={isSubmitting ? "Subiendo..." : "Subir Comprobante"}
							bgColor="green"
							disabled={!selectedFile || isSubmitting}
						/>
					</div>
				</Form>
			)}
		</Formik>
	);
};

export default ModalSubirComprobante;